import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Eye, Mail, ExternalLink } from 'lucide-react';

export default function Admin() {
  const [subs, setSubs] = useState([]);
  const [users, setUsers] = useState([]);
  const [emails, setEmails] = useState({ logs: [], sender_email: '', api_key_configured: false });
  const [viewing, setViewing] = useState(null);
  const [viewingEmail, setViewingEmail] = useState(null);

  const load = async () => {
    const [s, u, e] = await Promise.all([
      api.get('/admin/kyc/pending'),
      api.get('/admin/users'),
      api.get('/admin/email_log?limit=200'),
    ]);
    setSubs(s.data.submissions);
    setUsers(u.data.users);
    setEmails(e.data);
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    try { await api.post(`/admin/kyc/${id}/approve`); toast.success('KYC approved'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const reject = async (id) => {
    try { await api.post(`/admin/kyc/${id}/reject`); toast.success('KYC rejected'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="max-w-[1440px] mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-display text-white text-4xl font-bold tracking-tighter">Admin Panel</h1>
      <p className="text-[#848E9C] text-sm mt-1 mb-8">KYC approvals · user management · email delivery</p>

      <Tabs defaultValue="kyc">
        <TabsList className="bg-[#181A20] border border-[#2B3139] rounded-sm mb-6">
          <TabsTrigger value="kyc" className="data-[state=active]:bg-[#FCD535] data-[state=active]:text-[#0B0E11] rounded-none px-4" data-testid="admin-tab-kyc">KYC Queue ({subs.length})</TabsTrigger>
          <TabsTrigger value="users" className="data-[state=active]:bg-[#FCD535] data-[state=active]:text-[#0B0E11] rounded-none px-4" data-testid="admin-tab-users">Users ({users.length})</TabsTrigger>
          <TabsTrigger value="emails" className="data-[state=active]:bg-[#FCD535] data-[state=active]:text-[#0B0E11] rounded-none px-4" data-testid="admin-tab-emails">Email Log ({emails.logs.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="kyc">
      <div className="border border-[#2B3139] bg-[#181A20]" data-testid="admin-kyc-table">
        <div className="grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-wider text-[#848E9C] border-b border-[#2B3139]">
          <div className="col-span-3">Email</div>
          <div className="col-span-3">Full Name</div>
          <div className="col-span-2">Country</div>
          <div className="col-span-1">Doc</div>
          <div className="col-span-3 text-right">Actions</div>
        </div>
        {subs.length === 0 ? (
          <div className="p-8 text-center text-[#848E9C] text-sm">No pending submissions.</div>
        ) : subs.map(s => (
          <div key={s.kyc_id} className="grid grid-cols-12 px-4 py-3 border-b border-[#2B3139]/60 items-center">
            <div className="col-span-3 text-white text-sm truncate">{s.email}</div>
            <div className="col-span-3 text-[#EAECEF] text-sm">{s.full_name}</div>
            <div className="col-span-2 text-[#B7BDC6] text-sm">{s.country}</div>
            <div className="col-span-1 text-[#848E9C] text-xs uppercase">{s.document_type}</div>
            <div className="col-span-3 flex justify-end gap-2">
              <Dialog>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline" className="border-[#2B3139] bg-transparent text-white hover:bg-[#2B3139] rounded-sm" data-testid={`view-kyc-${s.kyc_id}`} onClick={()=>setViewing(s)}>
                    <Eye className="w-3.5 h-3.5" />
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#181A20] border-[#2B3139] text-white rounded-sm max-w-2xl max-h-[85vh] overflow-y-auto">
                  <DialogHeader><DialogTitle>KYC — {viewing?.full_name}</DialogTitle></DialogHeader>
                  {viewing && (
                    <div className="space-y-4 text-sm">
                      <div className="grid grid-cols-2 gap-3">
                        <div><div className="text-[#848E9C] text-xs uppercase">Email</div><div>{viewing.email}</div></div>
                        <div><div className="text-[#848E9C] text-xs uppercase">Country</div><div>{viewing.country}</div></div>
                        <div><div className="text-[#848E9C] text-xs uppercase">Doc Type</div><div className="capitalize">{viewing.document_type}</div></div>
                        <div><div className="text-[#848E9C] text-xs uppercase">Doc Number</div><div className="font-mono">{viewing.document_number}</div></div>
                      </div>
                      <div>
                        <div className="text-[#848E9C] text-xs uppercase mb-1">ID Front</div>
                        <img src={viewing.id_front_base64} alt="front" className="max-h-64 border border-[#2B3139]" />
                      </div>
                      {viewing.id_back_base64 && (
                        <div>
                          <div className="text-[#848E9C] text-xs uppercase mb-1">ID Back</div>
                          <img src={viewing.id_back_base64} alt="back" className="max-h-64 border border-[#2B3139]" />
                        </div>
                      )}
                      <div>
                        <div className="text-[#848E9C] text-xs uppercase mb-1">Selfie</div>
                        <img src={viewing.selfie_base64} alt="selfie" className="max-h-64 border border-[#2B3139]" />
                      </div>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
              <Button size="sm" onClick={() => approve(s.kyc_id)} className="bg-[#0ECB81] hover:bg-[#0BB574] text-white rounded-sm" data-testid={`approve-kyc-${s.kyc_id}`}>
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
              </Button>
              <Button size="sm" onClick={() => reject(s.kyc_id)} className="bg-[#F6465D] hover:bg-[#E33A50] text-white rounded-sm" data-testid={`reject-kyc-${s.kyc_id}`}>
                <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
              </Button>
            </div>
          </div>
        ))}
      </div>
      </TabsContent>

      <TabsContent value="users">
      <h2 className="text-white text-xl font-semibold mb-3">All Users ({users.length})</h2>
      <div className="border border-[#2B3139] bg-[#181A20]">
        <div className="grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-wider text-[#848E9C] border-b border-[#2B3139]">
          <div className="col-span-4">Email</div>
          <div className="col-span-3">Name</div>
          <div className="col-span-2">Role</div>
          <div className="col-span-2">KYC</div>
          <div className="col-span-1 text-right">Auth</div>
        </div>
        {users.map(u => (
          <div key={u.user_id} className="grid grid-cols-12 px-4 py-3 border-b border-[#2B3139]/60 text-sm">
            <div className="col-span-4 text-white truncate">{u.email}</div>
            <div className="col-span-3 text-[#EAECEF]">{u.name}</div>
            <div className="col-span-2 text-[#B7BDC6] uppercase text-xs">{u.role}</div>
            <div className="col-span-2 text-xs uppercase" style={{ color: u.kyc_status === 'verified' ? '#0ECB81' : u.kyc_status === 'pending' ? '#FCD535' : u.kyc_status === 'rejected' ? '#F6465D' : '#848E9C' }}>{u.kyc_status}</div>
            <div className="col-span-1 text-right text-[#848E9C] text-xs">{u.auth_provider}</div>
          </div>
        ))}
      </div>
      </TabsContent>

      <TabsContent value="emails">
        {/* Setup banner */}
        <div className="bg-[#181A20] border border-[#2B3139] p-5 mb-4 flex items-start gap-3">
          <Mail className="w-5 h-5 text-[#FCD535] flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">
            <div className="text-white font-semibold mb-1">Email Provider — Resend</div>
            <div className="text-[#B7BDC6]">
              Current sender: <code className="text-[#FCD535] font-mono">{emails.sender_email}</code>
              {' · '}
              API key: <span className={emails.api_key_configured ? 'text-[#0ECB81]' : 'text-[#F6465D]'}>{emails.api_key_configured ? 'configured' : 'missing'}</span>
            </div>
            {emails.sender_email === 'onboarding@resend.dev' && (
              <div className="text-[#B7BDC6] text-xs mt-2">
                <strong className="text-white">To deliver to real recipients:</strong> Verify a sender domain at{' '}
                <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="text-[#FCD535] hover:underline inline-flex items-center gap-1">
                  resend.com/domains <ExternalLink className="w-3 h-3" />
                </a>{' '}
                (add DNS records shown), then update <code className="text-[#FCD535] font-mono">SENDER_EMAIL</code> in <code className="font-mono">backend/.env</code> to <code className="font-mono">noreply@yourdomain.com</code> and restart backend.
              </div>
            )}
          </div>
        </div>

        <div className="border border-[#2B3139] bg-[#181A20]" data-testid="email-log-table">
          <div className="grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-wider text-[#848E9C] border-b border-[#2B3139]">
            <div className="col-span-3">To</div>
            <div className="col-span-4">Subject</div>
            <div className="col-span-3">Sent</div>
            <div className="col-span-2 text-right">Status</div>
          </div>
          {emails.logs.length === 0 ? (
            <div className="p-8 text-center text-[#848E9C] text-sm">No emails sent yet.</div>
          ) : emails.logs.map(l => (
            <Dialog key={l.email_log_id}>
              <DialogTrigger asChild>
                <button onClick={() => setViewingEmail(l)} className="grid grid-cols-12 px-4 py-3 border-b border-[#2B3139]/60 items-center text-sm w-full text-left hover:bg-[#2B3139]" data-testid={`email-row-${l.email_log_id}`}>
                  <div className="col-span-3 text-white truncate">{l.to}</div>
                  <div className="col-span-4 text-[#EAECEF] truncate">{l.subject}</div>
                  <div className="col-span-3 text-[#848E9C] text-xs">{new Date(l.sent_at).toLocaleString()}</div>
                  <div className="col-span-2 text-right text-xs uppercase font-semibold" style={{ color: l.status === 'sent' ? '#0ECB81' : l.status === 'failed' ? '#F6465D' : '#848E9C' }}>
                    {l.status}
                  </div>
                </button>
              </DialogTrigger>
              <DialogContent className="bg-[#181A20] border-[#2B3139] text-white rounded-sm max-w-2xl">
                <DialogHeader><DialogTitle className="truncate">{viewingEmail?.subject}</DialogTitle></DialogHeader>
                {viewingEmail && (
                  <div className="space-y-3 text-sm">
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div><span className="text-[#848E9C]">To:</span> <span className="text-white">{viewingEmail.to}</span></div>
                      <div><span className="text-[#848E9C]">Status:</span> <span className="font-semibold" style={{ color: viewingEmail.status === 'sent' ? '#0ECB81' : viewingEmail.status === 'failed' ? '#F6465D' : '#848E9C' }}>{viewingEmail.status}</span></div>
                    </div>
                    {viewingEmail.error && (
                      <div className="text-[#F6465D] text-xs bg-[#F6465D]/10 p-3 border border-[#F6465D]/30">
                        {viewingEmail.error}
                      </div>
                    )}
                    <div className="text-[#848E9C] text-xs uppercase tracking-wider">Provider ID</div>
                    <div className="font-mono text-xs text-[#B7BDC6]">{viewingEmail.provider_id || '—'}</div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          ))}
        </div>
      </TabsContent>
    </Tabs>
    </div>
  );
}
